import React, { useEffect, useMemo, useState } from "react";
import { FiSettings, FiBell, FiShield, FiSliders } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import notify from "../utils/toast";

/**
 * Settings
 *
 * Previously this screen looked finished but did nothing: the inputs were
 * uncontrolled and empty on every visit, "Save Settings" had no click handler
 * at all, and "Cancel" did nothing either — so an admin could type a support
 * email, hit save, get no feedback, and lose the change on navigation.
 *
 * What changed here:
 *  - every field is controlled and the whole form round-trips through
 *    localStorage, so values survive a reload;
 *  - save validates first and reports through a toast instead of a browser
 *    alert, and the action is named the same thing in the button and in the
 *    confirmation ("Save changes" -> "Changes saved");
 *  - the save bar only activates when something actually changed, and Cancel
 *    restores the last saved state;
 *  - sections are reachable from a left rail instead of stacking four
 *    identical cards down the page.
 *
 * Storage key is local to the admin UI. No API contract was invented here —
 * when a settings endpoint exists, swap `persist()` for the call.
 */

const STORAGE_KEY = "adminSettings";

const DEFAULTS = {
  companyName: "",
  supportEmail: "",
  phoneNumber: "",
  language: "English",
  emailNotification: true,
  smsNotification: false,
  twoFactorAuth: true,
  autoUpdate: true,
};

const SECTIONS = [
  { id: "general", label: "General", icon: <FiSliders /> },
  { id: "notifications", label: "Notifications", icon: <FiBell /> },
  { id: "security", label: "Security", icon: <FiShield /> },
  { id: "appearance", label: "Appearance", icon: <FiSettings /> },
];

const readStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : { ...DEFAULTS };
  } catch {
    return { ...DEFAULTS };
  }
};

const validate = (values) => {
  const errors = {};

  if (!values.companyName.trim()) {
    errors.companyName = "Enter the name players see on receipts.";
  }

  if (!values.supportEmail.trim()) {
    errors.supportEmail = "Enter an email players can write to.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(values.supportEmail.trim())) {
    errors.supportEmail = "That doesn't look like a valid email address.";
  }

  if (values.phoneNumber && values.phoneNumber.length !== 10) {
    errors.phoneNumber = "Enter all 10 digits, or leave it blank.";
  }

  return errors;
};

const Settings = () => {
  const navigate = useNavigate();

  const [saved, setSaved] = useState(readStored);
  const [settings, setSettings] = useState(saved);
  const [errors, setErrors] = useState({});
  const [section, setSection] = useState("general");
  const [saving, setSaving] = useState(false);

  const dirty = useMemo(
    () => JSON.stringify(saved) !== JSON.stringify(settings),
    [saved, settings]
  );

  /* Warn before losing unsaved edits on a full page unload. */
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const setField = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleToggle = (key) => setField(key, !settings[key]);

  const handleSave = async () => {
    const found = validate(settings);
    setErrors(found);

    if (Object.keys(found).length > 0) {
      /* Send the admin to the section holding the first problem rather than
         leaving them to hunt for a red border on a collapsed panel. */
      setSection("general");
      notify.warning("Fix the highlighted fields, then save again.", "Not saved");
      return;
    }

    try {
      setSaving(true);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
      setSaved(settings);
      notify.success("Your settings have been updated.", "Changes saved");
    } catch (error) {
      notify.error(error, "Could not save your settings");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!dirty) return;

    const confirmed = await notify.confirm({
      title: "Discard changes?",
      text: "Your edits since the last save will be lost.",
      icon: "warning",
      confirmButtonText: "Discard",
    });

    if (!confirmed) return;

    setSettings(saved);
    setErrors({});
    notify.info("Back to your last saved settings.", "Changes discarded");
  };

  return (
    <div className="settings-page lead-page">
      <PageHeader
        icon={<FiSettings />}
        title="Settings"
        subtitle="Contact details, alerts and security for this admin console."
      />

      <div className="settings-layout">
        <nav className="settings-nav" aria-label="Settings sections">
          {SECTIONS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`settings-nav__item ${section === item.id ? "is-active" : ""}`}
              onClick={() => setSection(item.id)}
              aria-current={section === item.id ? "true" : undefined}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </nav>

        <div className="settings-panel">
          <div className="settings-panel__body">
            {/* ---------------- General ---------------- */}
            {section === "general" && (
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label" htmlFor="companyName">
                    Company name
                  </label>
                  <input
                    id="companyName"
                    type="text"
                    className={`form-control ${errors.companyName ? "is-invalid" : ""}`}
                    placeholder="Ram Rummy Gaming Pvt Ltd"
                    value={settings.companyName}
                    onChange={(e) => setField("companyName", e.target.value)}
                  />
                  {errors.companyName ? (
                    <span className="settings-field-error">{errors.companyName}</span>
                  ) : (
                    <span className="settings-hint">Shown on receipts and emails.</span>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label" htmlFor="supportEmail">
                    Support email
                  </label>
                  <input
                    id="supportEmail"
                    type="email"
                    className={`form-control ${errors.supportEmail ? "is-invalid" : ""}`}
                    placeholder="support@ramrummy.com"
                    value={settings.supportEmail}
                    onChange={(e) => setField("supportEmail", e.target.value)}
                  />
                  {errors.supportEmail ? (
                    <span className="settings-field-error">{errors.supportEmail}</span>
                  ) : (
                    <span className="settings-hint">Where player replies land.</span>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label" htmlFor="phoneNumber">
                    Phone number
                  </label>
                  <input
                    id="phoneNumber"
                    type="tel"
                    inputMode="numeric"
                    className={`form-control ${errors.phoneNumber ? "is-invalid" : ""}`}
                    placeholder="10-digit mobile number"
                    maxLength={10}
                    value={settings.phoneNumber}
                    onChange={(e) =>
                      setField("phoneNumber", e.target.value.replace(/\D/g, "").slice(0, 10))
                    }
                  />
                  {errors.phoneNumber ? (
                    <span className="settings-field-error">{errors.phoneNumber}</span>
                  ) : (
                    <span className="settings-hint">Optional.</span>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label" htmlFor="language">
                    Language
                  </label>
                  <select
                    id="language"
                    className="form-select"
                    value={settings.language}
                    onChange={(e) => setField("language", e.target.value)}
                  >
                    <option>English</option>
                    <option>Hindi</option>
                    <option>French</option>
                  </select>
                  <span className="settings-hint">Applies to this admin console only.</span>
                </div>
              </div>
            )}

            {/* ---------------- Notifications ---------------- */}
            {section === "notifications" && (
              <>
                <div className="settings-toggle">
                  <div className="settings-toggle__text">
                    <h3>Email notifications</h3>
                    <p>Get deposit, withdrawal and KYC alerts by email.</p>
                  </div>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      aria-label="Email notifications"
                      checked={settings.emailNotification}
                      onChange={() => handleToggle("emailNotification")}
                    />
                  </div>
                </div>

                <div className="settings-toggle">
                  <div className="settings-toggle__text">
                    <h3>SMS notifications</h3>
                    <p>Send the same alerts to the registered mobile number.</p>
                  </div>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      aria-label="SMS notifications"
                      checked={settings.smsNotification}
                      onChange={() => handleToggle("smsNotification")}
                    />
                  </div>
                </div>
              </>
            )}

            {/* ---------------- Security ---------------- */}
            {section === "security" && (
              <>
                <div className="settings-toggle">
                  <div className="settings-toggle__text">
                    <h3>Two-factor authentication</h3>
                    <p>Ask for a one-time code in addition to the password at sign-in.</p>
                  </div>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      aria-label="Two-factor authentication"
                      checked={settings.twoFactorAuth}
                      onChange={() => handleToggle("twoFactorAuth")}
                    />
                  </div>
                </div>

                <div className="settings-toggle">
                  <div className="settings-toggle__text">
                    <h3>Password</h3>
                    <p>Change the password used for this admin account.</p>
                  </div>
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => navigate("/profile")}
                  >
                    Change password
                  </button>
                </div>
              </>
            )}

            {/* ---------------- Appearance ---------------- */}
            {section === "appearance" && (
              <div className="settings-toggle">
                <div className="settings-toggle__text">
                  <h3>Automatic updates</h3>
                  <p>Install console updates as soon as they are released.</p>
                </div>
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    aria-label="Automatic updates"
                    checked={settings.autoUpdate}
                    onChange={() => handleToggle("autoUpdate")}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="settings-savebar">
        <span className={`settings-savebar__note ${dirty ? "is-dirty" : ""}`}>
          {dirty ? "You have unsaved changes." : "All changes saved."}
        </span>
        <div className="settings-savebar__actions">
          <button
            className="btn btn-light border"
            onClick={handleCancel}
            disabled={!dirty || saving}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!dirty || saving}
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
